import EntityIcon from 'Components/Atoms/EntityIcon/EntityIcon';
import Grid from 'Components/Atoms/Grid/Grid';
import { Icon, ICONS } from 'Components/Atoms/Icon/Icon';
import RowItem from 'Components/Atoms/Row/RowItem';
import RowItemInfo from 'Components/Atoms/Row/RowItemInfo';
import RowItemTitle from 'Components/Atoms/Row/RowItemTitle';
import Title from 'Components/Atoms/Title/Title';
import EditButton from 'Components/Molecules/EditButton/EditButton';
import FormGroup from 'Components/Molecules/Form/FormGroup/FormGroup';
import Field from 'Pages/Dashboard/Settings/FieldsManagement/Fields/FieldTypes/Field';
import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { getInternalDatasetIdByName } from 'Services/helpers';
import { RelationService } from 'Services/Relation/Application/RelationService';
import EntityHelper from 'Services/Settings/Entity/EntityHelper';
import { updateBusinessUnit } from 'Store/BusinessUnit/Actions';
import { getBusinessUnitById, getBusinessUnitDatasetKeys } from 'Store/BusinessUnit/Selectors';
import { updateCandidate } from 'Store/Candidate/Actions';
import { getCandidateById, getCandidateDatasetKeys } from 'Store/Candidate/Selectors';
import { updateContact } from 'Store/Contact/Actions';
import { getContactById, getContactDatasetKeys } from 'Store/Contact/Selectors';
import { saveRelation } from 'Store/FieldManagement/Actions';
import { FieldType } from 'Store/FieldManagement/PropTypes';
import { StoreState } from 'Store/Reducers';
import { getEntitiesById, getEntityById } from 'Store/Settings/Entities/Selectors';
import { isEntityPatching } from 'Store/Shared/Store/Values/Selector';
import { updateVacancy } from 'Store/Vacancy/Actions';
import { getVacancyById, getVacancyDatasetKeys } from 'Store/Vacancy/Selectors';
import styles from './EntityRelationSidebar.module.sass';

const EntityRelationSidebar = ({
    id, entity_id, entity,
    entitySetting, entityById,
    vacancy, candidate, contact, business_unit,
    vacancyDatasetKeys, candidateDatasetKeys, contactDatasetKeys, businessUnitDatasetKeys,

    isEntityPatching,

    ...props
}: EntityRelationSidebarProps) => {
    const updateRecord = useMemo(() => EntityHelper.update(props, entity.name), []);
    const [state, setState] = useState({
        isEdit: false,
        isRelationFetching: false,
        isPreviewLoading: true,
        isPatching: false
    });

    const initialData = useMemo(() => ({
        ...vacancy?.relations,
        ...candidate?.relations,
        ...contact?.relations,
        ...business_unit?.relations
    }), [vacancy, candidate, contact, business_unit]);

    const [data, setData] = useState(initialData);
    const [relationPreviewData, setRelationPreviewData] = useState([]);

    const fetchRelationPreviewData = async () => {
        const relationService = new RelationService();
        const rawRelationValues = await relationService.fetchRelations([id]);
        setRelationPreviewData(rawRelationValues);
        setState((state) => ({
            ...state,
            isPreviewLoading: false
        }));
    }

    // Update data state when initialData changes (i.e., when relations are fetched)
    useEffect(() => {
        setData(initialData);
        fetchRelationPreviewData();
    }, [initialData]);

    const relationPreviews = useMemo(() => (entitySetting?.relations || [])
        .filter(i => i.isActive && !i.isEditable)
        .sort((a, b) => a.order - b.order)
    , [entitySetting?.relations]);

    useEffect(() => {
        fetchRelationPreviewData();
    }, []);

    const relationEditables = useMemo(() => (entitySetting?.relations || [])
        .filter(i => i.isActive && i.isEditable)
        .sort((a, b) => a.order - b.order)
    , [entitySetting?.relations]);

    const handleSave = () => {
        updateRecord(id, {
            relations: data
        });
        const relations = relationEditables?.reduce((a, c) => a = [...(a || []), ...(data?.[c.id] || [])], []);
        props.saveRelation({
            dataset_key: id,
            relations: relations
        });
    };

    const handleEdit = (value: boolean) => {
        setState((state) => ({
            ...state,
            isEdit: value
        }));

        if (!value) setData(initialData);
    }

    const handleRelationChange = async (currentIteration, maxIteration, values) => {
        const id = relationEditables?.[currentIteration]?.id;
        const aboveRelationValues = values?.[id];

        const underFieldId = relationEditables?.[currentIteration+1]?.id;
        const underFieldValues = [...(values?.[underFieldId] || [])];

        let newUnderFieldValues: any = [];
        if (aboveRelationValues && aboveRelationValues?.length > 0) {
            const relationService = new RelationService();
            const relationValues = await relationService.fetchRelations(aboveRelationValues);

            newUnderFieldValues = underFieldValues?.filter(i => relationValues?.includes(i));
        }

        if (underFieldId) {
            values = {
                ...values,
                [underFieldId]: newUnderFieldValues
            }
        }

        if (currentIteration >= maxIteration) {
            setState({
                ...state,
                isRelationFetching: false
            });
            setData(values);

            return;
        }

        handleRelationChange(currentIteration+1, maxIteration, {...values});
    }

    const handleChange = (idx, key, value) => {
        const newValues = {
            ...data,   
            [key]: value
        };

        const fieldLength = relationEditables?.length-1;
        const nextFieldId = relationEditables?.[idx+1]?.id;
        
        const isLastItem = idx >= fieldLength;

        if (!isLastItem && (nextFieldId && data?.[nextFieldId]?.length > 0)) {
            setState({
                ...state,
                isRelationFetching: true
            });

            handleRelationChange(idx, fieldLength, newValues);

            return;
        }

        setData(newValues);
    }

    const getDatasetByName = (name) => {
        switch (name) {
            case 'vacancy':
                return vacancyDatasetKeys;
            case 'candidate':
                return candidateDatasetKeys;
            case 'contact':
                return contactDatasetKeys;
            case 'business_unit':
                return businessUnitDatasetKeys;
            default:
                return {};
        }
    }

    if (relationEditables?.length === 0 && relationPreviews?.length === 0) {
        return <></>;
    }

    return <div className={styles['entityrelation']}>
        {
            relationEditables.length > 0
            && <Grid
                position='between'
                align='center'
                axis='horizontal'
            >
                <Title weight='semibold'>
                    Relaties
                </Title>
                <EditButton
                    variant='button'
                    isEdit={state.isEdit}
                    onChange={handleEdit}
                    onSave={handleSave}
                    isLoading={isEntityPatching}
                />
            </Grid>
        }


        {
            relationEditables.length > 0 &&
            <div className={styles['entityrelationsidebar']}>
                {
                    relationEditables.map((i, idx) => {
                        const aboveFieldId = relationEditables?.[idx-1]?.id;
                        const isDisabled = !aboveFieldId ? false : !(data?.[aboveFieldId] && data?.[aboveFieldId]?.length > 0);

                        const relation = aboveFieldId ? data?.[aboveFieldId] : [];

                        return <FormGroup
                            iconBefore={<EntityIcon entity={entityById?.[i.id]?.name} size='small' />}
                            title={EntityHelper.getDisplayValueByName(entityById?.[i.id]?.name)}
                        >
                            <Field
                                isEdit={state.isEdit}
                                type={FieldType.Multi_Selector}
                                value={data?.[i.id] || []}
                                onChange={(value) => handleChange(idx, i.id, value)}
                                fieldProperties={{
                                    alignLeft: true,
                                    dataset: [getInternalDatasetIdByName(entityById?.[i.id]?.name)],
                                    isDisabled: isDisabled,
                                    inRelation: relation
                                }}
                            />
                        </FormGroup>;
                    })
                }
            </div>
        }
        {
            !state.isPreviewLoading &&
            relationPreviews.length > 0 &&
            <div className={styles['entityrelationsidebar']}>
                {
                    relationPreviews.map(i => 
                        <FormGroup
                            iconBefore={<EntityIcon entity={entityById?.[i.id]?.name} size='small' />}
                            title={EntityHelper.getDisplayValueByName(entityById?.[i.id]?.name)}
                        >
                            <Field
                                isEdit={false}
                                type={FieldType.Multi_Selector}
                                value={relationPreviewData.filter(j => getDatasetByName(entityById?.[i.id]?.name).includes(j))}
                                fieldProperties={{
                                    dataset: [getInternalDatasetIdByName(entityById?.[i.id]?.name)],
                                    alignLeft: true
                                }}
                            />
                        </FormGroup>
                    )
                }
            </div>
        }
    </div>;
}

interface Props {
    id: string
    entity_id: string
    entity: any
};

const mapState = (state: StoreState, props: Props) => ({
    entityById: getEntitiesById(state),
    entitySetting: getEntityById(state, props?.entity_id),

    vacancy: getVacancyById(state, props?.id),
    candidate: getCandidateById(state, props?.id),
    business_unit: getBusinessUnitById(state, props?.id),
    contact: getContactById(state, props?.id),
    
    vacancyDatasetKeys: getVacancyDatasetKeys(state) || [],
    candidateDatasetKeys: getCandidateDatasetKeys(state) || [],
    contactDatasetKeys: getContactDatasetKeys(state) || [],
    businessUnitDatasetKeys: getBusinessUnitDatasetKeys(state) || [],

    isEntityPatching: isEntityPatching(state, getEntityById(state, props?.entity_id)?.name)
});

const mapDispatch = {
    updateVacancy,
    updateContact,
    updateCandidate,
    updateBusinessUnit,

    saveRelation
}

type EntityRelationSidebarProps = Props & ReturnType<typeof mapState> & typeof mapDispatch;

export default connect(mapState, mapDispatch)(EntityRelationSidebar);